import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import React, { useEffect, useState, useCallback, Dispatch, SetStateAction } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import { isDate, isEqual } from 'lodash';
import formatDate from '../../lib/formatDate';
import { Button, Col, Container, FormGroup, Input, Label, Modal, ModalBody, Row } from 'reactstrap';
import { VLDatePicker } from '../VLDatePicker';
import useApi from '../../hooks/useApi';
import { tools } from '../../lib/service.tools';
import { AlertMessage } from '../AlertMessage/AlertMessage';
import { LoadingButton } from '../LoadingButton';
import { PostcodeAutocomplete } from '../PostcodeAutocomplete';
import { PatientTags } from '../PatientTags/PatientTags';
import differenceInYears from 'date-fns/differenceInYears';
import moment from 'moment-timezone';
import * as nhsValidator from 'nhs-number-validator';
import useIsClientAdmin from '../../hooks/useIsClientAdmin';
import useFetchEmployerPatientMeta from '../../hooks/useFetchEmployerPatientMeta';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getRandomInt, sanitiseValueFromAngleBrackets } from '../../utils';
import { getMetaMiscQuery } from 'vl-common/src/schemas/getMetaMisc';
import { getMetaUsersQuery } from 'vl-common/src/schemas/getMetaUsers';
import useFetchClientList from '../../hooks/useFetchClientList';

const removeSpaces = (value) => value.replace(/\s/g, '');

export function useMembershipNumberChecker(clientList) {
  const { data: metaValidators } = useQuery({ ...getMetaMiscQuery(), select: (d) => d[0].field_validators });
  return useCallback(
    (copiedData = { client_id: '', membership_no: '' }) => {
      const isNHSclient = clientList?.filter((client) => client.client_id === parseInt(copiedData?.client_id))?.[0]
        ?.nhs_client;
      const isNHSnumberValid = nhsValidator.validate(copiedData.membership_no);
      if (isNHSclient && !isNHSnumberValid) {
        return { valid: false, message: 'NHS number not valid' };
      } else if (!isNHSclient) {
        const clientCode = clientList.find(
          (client: any) => client.client_id === parseInt(copiedData?.client_id)
        )?.client_code;
        const membershipNumberValidator = metaValidators
          ?.filter((validator) => validator.rule_code === 'MEMBERSHIP_NO')
          ?.find((validator) => validator.client_code === clientCode);
        if (
          membershipNumberValidator?.regex &&
          !new RegExp(membershipNumberValidator?.regex).test(copiedData.membership_no)
        ) {
          return {
            valid: false,
            message: membershipNumberValidator.sample_data
              ? `Membership number has to be in the format ${membershipNumberValidator.sample_data}`
              : 'Membership number is invalid'
          };
        }
        if (
          membershipNumberValidator?.field_max_length &&
          copiedData.membership_no?.length > membershipNumberValidator?.field_max_length
        ) {
          return {
            valid: false,
            message: `Membership number max length is ${membershipNumberValidator?.field_max_length}`
          };
        }
      }

      return { valid: true, message: '' };
    },
    [clientList, metaValidators]
  );
}

type PatientFormData = {
  new_user_guid?: string;
  membership_no: string;
  title: string;
  first_name: string;
  last_name: string;
  pref_name?: string;
  email: string;
  dob: string;
  sex: string;
  tel_mobile_no: string;
  tel_other_no: string;
  pref_tel: string;
  pref_notification: string;
  avatar_url: string;
  addline1: string;
  addline2: string;
  addline3: string;
  addline4: string;
  region: string;
  postcode: string;
  country: string;
  employer_code: string;
  insured: boolean;
  self_refer?: boolean;
  ess_comms_only?: boolean;
  lang_code?: string;
  timezone?: string;
  sex_code?: string;
  sex_desc?: string;
  client_id?: string;
  user_guid?: string;
};

const emptyForm: PatientFormData = {
  new_user_guid: '',
  membership_no: '',
  title: '',
  first_name: '',
  last_name: '',
  pref_name: '',
  email: '',
  dob: '0000-00-00',
  sex: '',
  sex_code: '',
  sex_desc: '',
  tel_mobile_no: '',
  tel_other_no: '',
  pref_tel: 'O',
  pref_notification: '',
  avatar_url: '',
  addline1: '',
  addline2: '',
  addline3: '',
  addline4: '',
  region: '',
  postcode: '',
  country: '',
  employer_code: '',
  insured: true
};

export const EditPatientDetails = ({
  close,
  readonly = false,
  fullPageMode = false,
  savedPatientDetails,
  onCreated = (memberdata?: unknown) => {},
  cancel = () => {},
  setSize = () => {},
  size,
  showExpandOption = false
}: {
  close: any;
  readonly?: boolean;
  fullPageMode?: boolean;
  savedPatientDetails: any;
  onCreated: (memberdata: unknown) => void;
  cancel?: () => void;
  setSize: Dispatch<SetStateAction<string>>;
  size: any;
  showExpandOption: boolean;
}) => {
  const { t } = useTranslation('common');
  const { Api, consumed } = useApi();
  const ro_fields = savedPatientDetails?.ro_fields || [];
  const queryClient = useQueryClient();

  const { data: metaMisc } = useQuery(getMetaMiscQuery());
  const { data: userMeta } = useQuery(getMetaUsersQuery());

  const metaTimezones = metaMisc?.timezones || [];
  const { titles, sexes } = userMeta || { titles: [], sexes: [] };

  const clientList = useFetchClientList({ exclude_hbs: true });

  const checkMembershipNoValid = useMembershipNumberChecker(clientList);
  const isClientAdmin = useIsClientAdmin();
  const [formData, setFormData] = useState(emptyForm);
  const [activeField, setActiveField] = useState(false);
  const [primaryContactNumber, setPrimaryContactNumber] = useState('');
  const [alternativeContactNumber, setAlternativeContactNumber] = useState('');
  const [dpDate, setDpDate] = useState<Date | null>(null);
  const [language, setLanguage] = useState<{ label: string; value: string } | undefined>();
  const [timezone, setTimezone] = useState<{ label: string; value: string } | undefined>({
    value: 'Europe/London',
    label: 'Europe/London'
  });
  const [tags, setTags] = useState(null);
  const [filterTags, setFilterTags] = useState(null);
  const [selfRef, setSelfRef] = useState(false);
  const [essCommsOnly, setEssCommsOnly] = useState(false);
  const [notificationChange, setNotificationChange] = useState(false);
  const [clientId, setClientId] = useState('');
  const employerList = useFetchEmployerPatientMeta(clientId);
  const [adminCreatingDuplicateOfUser, setAdminCreatingDuplicateOfUser] = useState({});

  useEffect(() => {
    if (clientList?.length === 1 && !savedPatientDetails) {
      onChangeClient(`${clientList[0].client_id}`);
    }
  }, [clientList]);

  const clientSupportsInfoTags = clientList?.find(
    (client) => client?.client_id?.toString() === clientId?.toString()
  )?.info_tags;

  const overrideNotProvidedSexData = (sex: string) => {
    if (!['M', 'F'].includes(sex)) {
      return {
        sex: '',
        sex_code: '',
        sex_desc: ''
      };
    }

    return {};
  };

  useEffect(() => {
    if (savedPatientDetails) {
      setSelfRef(savedPatientDetails.self_refer);
      setEssCommsOnly(savedPatientDetails.ess_comms_only);
      setLanguage({ value: savedPatientDetails?.lang_code, label: savedPatientDetails.lang_desc });
      setTimezone({ value: savedPatientDetails?.timezone, label: savedPatientDetails.timezone });
      setFormData({ ...savedPatientDetails, ...overrideNotProvidedSexData(savedPatientDetails.sex) });

      setDpDate(savedPatientDetails.dob ? new Date(savedPatientDetails.dob) : null);
      setPrimaryContactNumber(
        savedPatientDetails.tel_mobile_no ? '+' + savedPatientDetails.tel_mobile_no?.replace(' ', '').trim() : ''
      );
      setAlternativeContactNumber(
        savedPatientDetails.tel_other_no ? '+' + savedPatientDetails.tel_other_no?.replace(' ', '').trim() : ''
      );
      setActiveField(true);
      onChangeClient(savedPatientDetails.client_id);
    }
  }, [savedPatientDetails]);

  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });
  const [saveInProgress, setSaveInProgress] = useState(false);

  const toggle = () => {
    close();
    setFormData(emptyForm);
    setAlternativeContactNumber('');
    setPrimaryContactNumber('');
    setDpDate(null);
    onChangeClient('');
    setActiveField(false);
    setSaveInProgress(false);
  };

  const createNewPatient = async (patientData) => {
    if (!isClientAdmin) {
      if (tags?.length < 1 && patientData.insured !== false && clientSupportsInfoTags) {
        setSaveInProgress(false);
        displayAlertMessage('Please select one or more tags');
        return false;
      }
    }
    patientData.membership_no = removeSpaces(patientData.membership_no);
    const membershipNoValidResult = checkMembershipNoValid(patientData);
    if (!membershipNoValidResult.valid) {
      setSaveInProgress(false);
      displayAlertMessage(membershipNoValidResult.message);
      return false;
    }

    let addPatientData: {
      email: string;
      first_name: string;
      last_name: string;
      pref_name?: string;
      membership_no: string;
      tel_mobile_no: string;
      tel_other_no: string;
      self_reg: boolean;
      client_id: string;
      title: string;
      employer_code: string;
      self_refer: boolean;
      ess_comms_only: boolean;
      dob?: Date;
    } = {
      email: patientData.email,
      first_name: patientData.first_name,
      last_name: patientData.last_name,
      pref_name: patientData.pref_name,
      membership_no: removeSpaces(patientData.membership_no),
      tel_mobile_no: patientData.tel_mobile_no,
      tel_other_no: patientData.tel_other_no,
      self_reg: true,
      client_id: patientData.client_id,
      title: patientData.title,
      employer_code: patientData.employer_code,
      self_refer: selfRef,
      ess_comms_only: essCommsOnly
    };

    if (size === 'full') {
      addPatientData = {
        ...patientData,
        ...addPatientData,
        self_reg: false
      };
    }

    const newUserMainKeys = {
      dob: addPatientData?.dob,
      name: `${addPatientData.first_name} ${addPatientData.last_name}`
    };
    // not all created users have DoB so we cannot say that it's the same user without knowing his dob
    if (newUserMainKeys?.dob && isDate(new Date(newUserMainKeys?.dob))) {
      const res = await Api.referrer.getPatientInfo(newUserMainKeys);
      if (res.status === 200 && !isEqual(newUserMainKeys, adminCreatingDuplicateOfUser)) {
        setSaveInProgress(false);
        const users = res?.data?.users;
        if (users?.length) {
          const { email } = users[0];
          displayAlertMessage(
            `Be aware that this patient may already exist: ${newUserMainKeys.name}, date of birth: ${formatDate(
              new Date(newUserMainKeys.dob),
              'dd-MM-yyyy'
            )} email: ${email}. You may proceed creating user`,
            30000
          );
          setAdminCreatingDuplicateOfUser(newUserMainKeys);
          return null;
        }
      }
    }

    try {
      const res = await Api.referrer.AddMember(addPatientData);

      if (res.status === 201) {
        setAdminCreatingDuplicateOfUser({});
        const params = {
          pat_guid: res.data.new_user_guid
        };

        // we try to add tags, BUT if it is failing it wont interrupt whole process and will close modal
        if (tags?.length || filterTags?.filterTags) {
          try {
            await Api.tags.setTags(res.data.new_user_guid, tags, filterTags);
          } catch {
            toast.error(<p>{t('Trouble saving patient tags')}</p>, { duration: 5000 });
          }
        }

        await Api.referrer
          .search(params)
          .then((searchRes) => {
            const { users } = searchRes;
            const new_member = users[0];

            setSaveInProgress(false);
            toggle();

            if (onCreated) {
              onCreated(new_member);
            }
          })
          .catch((error) => {
            setSaveInProgress(false);
            displayAlertMessage(
              'Patient created - you are only able to view this patient with the appropriate referrer profile access rights'
            );
            consumed(error);
          });
      }
    } catch (error) {
      setSaveInProgress(false);

      if (error.response?.data?.message) {
        displayAlertMessage(error.response.data.message);
      } else {
        displayAlertMessage('Failed to create patient, please try again.');
      }

      consumed(error);
    }
  };

  const updateExistingPatient = async (updatedPatientData) => {
    const {
      user_guid: up_user_guid,
      tel_mobile_no: tel_mobile,
      tel_other_no: tel_other,
      pref_tel: tel_pref,
      membership_no: mem_no,
      ...rest
    } = updatedPatientData;
    const editPatientBody = {
      up_user_guid,
      tel_mobile,
      tel_other,
      tel_pref,
      mem_no: removeSpaces(mem_no),
      ...rest
    };

    if (!isClientAdmin) {
      if (tags?.length < 1 && updatedPatientData.insured !== false && clientSupportsInfoTags) {
        setSaveInProgress(false);
        displayAlertMessage('Please select one or more tags');
        return false;
      }
    }

    const membershipNoValidResult = checkMembershipNoValid(updatedPatientData);
    if (!membershipNoValidResult.valid) {
      setSaveInProgress(false);
      displayAlertMessage(membershipNoValidResult.message);
      return false;
    }
    if (notificationChange) {
      const mobile = editPatientBody.tel_mobile.replace('+', '');
      const notificationSet = editPatientBody.pref_notification === 'SMS' ? 'S' : 'E';
      await Api.outputs.postNotificationPref({
        patient_guid: editPatientBody.up_user_guid,
        tel_mobile: mobile,
        method: notificationSet
      });
    }
    try {
      const result = await Api.referrer
        .member_editProfile(editPatientBody)
        .then(({ data }) => {
          if (editPatientBody.insured) {
            const { up_user_guid } = data;
            Api.tags
              .setTags(up_user_guid, tags, filterTags)
              .then(() => {
                toggle();
                if (onCreated) {
                  onCreated(data);
                }
              })
              .catch((error) => {
                displayAlertMessage('Trouble saving patient tags');
                consumed(error);
              });
          } else {
            toggle();
            onCreated?.(data);
          }
        })
        .catch((e) => {
          const backendMessage = e.response?.data?.message || 'Failed to save changes to patient, please try again.';
          const message = [412, 417].includes(e.response.status)
            ? 'Patient has outstanding cases, cannot be marked as uninsured'
            : backendMessage;

          displayAlertMessage(message);
          consumed(e);
        });

      return result;
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleSubmit = () => {
    if (!isFormValid()) return;
    setSaveInProgress(true);

    const dataToSave = { ...formData };
    dataToSave.tel_mobile_no = primaryContactNumber ? primaryContactNumber?.replace(' ', '').trim() : '';
    dataToSave.tel_other_no = alternativeContactNumber ? alternativeContactNumber?.replace(' ', '').trim() : '';
    dataToSave.pref_tel = primaryContactNumber ? 'M' : 'O';
    dataToSave.dob = moment(dpDate).format('YYYY-MM-DD');
    dataToSave.self_refer = selfRef;
    dataToSave.ess_comms_only = essCommsOnly;
    dataToSave.lang_code = language?.value;
    dataToSave.timezone = timezone?.value;
    dataToSave.sex = dataToSave.sex_code;
    dataToSave.client_id = clientId;

    if (savedPatientDetails) {
      updateExistingPatient(dataToSave);
      setFormData(dataToSave);
    } else {
      Api.referrer
        .checkMemberExist(dataToSave.email)
        .then((res) => {
          if (res.status === 204) {
            createNewPatient(dataToSave);
          } else {
            setSaveInProgress(false);
            displayAlertMessage('The email provided already exists.');
          }
        })
        .catch((e) => {
          createNewPatient(dataToSave);
          setFormData(dataToSave);
        });
    }

    queryClient.invalidateQueries({ queryKey: ['get-patient-list'] });
  };

  const handleManualPostcodeChange = (newPostcode: string) => {
    setFormData((prev) => ({ ...prev, postcode: newPostcode }));
  };

  const onSelect_postcode = (recordObject) => {
    if (recordObject.isAddressFound) {
      setFormData({
        ...formData,
        addline1: recordObject.line1,
        addline2: recordObject.line2,
        addline3: recordObject.line3,
        addline4: recordObject.town,
        region: recordObject.region,
        postcode: recordObject.postcode,
        country: recordObject.country
      });
      setActiveField(true);
    } else {
      setActiveField(true);
      setFormData({
        ...formData,
        addline1: '',
        addline2: '',
        addline3: '',
        addline4: '',
        region: '',
        country: '',
        postcode: recordObject.postcode
      });
      displayAlertMessage(recordObject.value);
    }
  };

  useEffect(() => {
    if (dpDate) {
      if (!isAgeLegal()) {
        displayAlertMessage('Patients must be over 18 years old to use this service.');
      }
    }
  }, [dpDate]);

  const displayAlertMessage = (msg = '', delay = 7000) => {
    setShowAlertMessage({ visibility: true, msg });
    const t = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg });
      clearTimeout(t);
    }, delay || 7000);
  };

  const isAgeLegal = () => {
    return differenceInYears(new Date(), dpDate) >= 18;
  };

  const isFormValid = () => {
    if (tools.isEmptyOrNull(formData.title)) {
      displayAlertMessage('Please select the title');
      return false;
    } else if (tools.isEmptyOrNull(formData.first_name)) {
      displayAlertMessage('Please fill the first name');
      return false;
    } else if (tools.isEmptyOrNull(formData.last_name)) {
      displayAlertMessage('Please fill the last name');
      return false;
    } else if (formData.email?.trim() === '') {
      displayAlertMessage('The email field is required');
      return false;
    } else if (!tools.isEmailValid(formData.email)) {
      displayAlertMessage('Email address is not valid');
      return false;
    } else if (tools.isEmptyOrNull(primaryContactNumber)) {
      displayAlertMessage('Please enter primary contact number');
      return false;
    } else if (primaryContactNumber && !isValidPhoneNumber(primaryContactNumber)) {
      displayAlertMessage('Please enter a valid primary contact number');
      return false;
    } else if (tools.isEmptyOrNull(formData.membership_no)) {
      displayAlertMessage('Please enter membership number');
      return false;
    } else if (tools.isEmptyOrNull(clientId)) {
      displayAlertMessage('Please select a client');
      return false;
    } else if (size === 'short') {
      return true;
    } else if (!isAgeLegal()) {
      displayAlertMessage(dpDate === null ? 'Please select date of birth' : 'Patient must be over 18 years of age');
      return false;
    } else if (tools.isEmptyOrNull(formData.sex) && tools.isEmptyOrNull(formData.sex_code)) {
      displayAlertMessage('Please select the sex');
      return false;
    } else if (tools.isEmptyOrNull(formData.addline1)) {
      displayAlertMessage('Please enter address line 1');
      return false;
    } else if (tools.isEmptyOrNull(formData.postcode)) {
      displayAlertMessage('Please enter a postcode');
      return false;
    } else if (clientSupportsInfoTags && !isClientAdmin && tools.isEmptyOrNull(tags)) {
      displayAlertMessage('Please select at least one info tag');
      return false;
    } else {
      return true;
    }
  };

  const RequiredField = () => (!readonly ? <span className="vl-product-satsuma">*</span> : <></>);

  const getTitle = (): string => {
    if (readonly) {
      return 'Details of';
    }
    return savedPatientDetails ? 'Edit' : 'Add';
  };

  const getButtonText = (): string => {
    if (savedPatientDetails) {
      if (saveInProgress) return 'Saving...';
      return 'Save';
    }

    if (saveInProgress) return 'Creating...';
    return 'Create';
  };

  const getIsSelfReferOptionDisabled = () => {
    if (!clientId) return false;

    const selectedClient = clientList.find((client) => client.client_id === Number(clientId));
    return !selectedClient?.self_refer;
  };

  const onChangeClient = (clientId: string | number) => {
    setClientId(clientId.toString());

    const clientSupportsSelfRefer = clientList.find((client) => client.client_id === Number(clientId))?.self_refer;
    if (!clientSupportsSelfRefer) {
      setSelfRef(false);
    }
  };

  if (size === 'short') {
    return (
      <Container fluid={true}>
        <Row>
          <Col xs={fullPageMode ? '10' : '12'}>
            <span className="vl-modal-title">{getTitle()} a</span>
            <span className="addm-member">{t('Patient')}</span>
          </Col>
          {!readonly && fullPageMode && (
            <Col xs="2">
              <Button
                className="vl-button vl-button-gray vl-font-regular"
                type="button"
                onClick={() => {
                  cancel?.();
                  toggle();
                }}
                data-testid="cancel-save-patient"
              >
                {t('Cancel')}
              </Button>

              <LoadingButton
                className="vl-button vl-font-regular"
                onClick={() => {
                  handleSubmit();
                }}
                loading={saveInProgress}
                disabled={saveInProgress}
                data-testid="save-patient"
              >
                {getButtonText()}
              </LoadingButton>
            </Col>
          )}
          <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
          <br />

          <Col xs="12">
            <form className="mt-5" data-testid="edit-patient-details">
              <fieldset disabled={readonly}>
                <Row className="addm-container">
                  <Col xs="3">
                    <FormGroup>
                      <input type="hidden" value="something" />
                      <Label for="title">
                        {t('Title')}
                        <RequiredField />
                      </Label>
                      <div className="addm-select">
                        <Input
                          id="title"
                          name="title"
                          data-testid="title-select-input"
                          value={formData.title || ''}
                          disabled={ro_fields.includes('title')}
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              title: event.target.value
                            })
                          }
                          type="select"
                          required
                        >
                          <option value="">{t('Select')}</option>
                          {titles.map((item, i) => {
                            return (
                              <option key={'t' + i} value={item.title}>
                                {item.title}
                              </option>
                            );
                          })}
                        </Input>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col xs="3">
                    <FormGroup>
                      <Label for="first_name">
                        {t('First Name')}
                        <RequiredField />
                      </Label>
                      <Input
                        className="addm-first-name addm-gray-ph"
                        name="first_name"
                        data-testid="first-name-input"
                        type="text"
                        autoComplete="fname"
                        value={formData.first_name || ''}
                        disabled={ro_fields.includes('first_name')}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            first_name: event.target.value
                          })
                        }
                        placeholder={t('Enter First Name')}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="3">
                    <FormGroup>
                      <Label for="last_name">
                        {t('Last Name')}
                        <RequiredField />
                      </Label>
                      <Input
                        className="addm-last-name addm-gray-ph"
                        id="last_name"
                        name="last_name"
                        data-testid="last-name-input"
                        type="text"
                        autoComplete="lname"
                        value={formData.last_name || ''}
                        disabled={ro_fields.includes('last_name')}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            last_name: event.target.value
                          })
                        }
                        placeholder={t('Enter Last Name')}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="3">
                    <FormGroup>
                      <Label for="pref_name">{t('Preferred Name')}</Label>
                      <Input
                        className="addm-pref-name addm-gray-ph"
                        id="pref_name"
                        name="pref_name"
                        data-testid="preferred-name-input"
                        type="text"
                        value={formData.pref_name || ''}
                        disabled={ro_fields.includes('pref_name')}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            pref_name: event.target.value
                          })
                        }
                        placeholder={t('Patient Preferred Name')}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="2">
                    <FormGroup>
                      <Label for="self-refer">
                        {t('Self Refer')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <div>
                        <Input
                          data-testid="self-refer-select-input"
                          type="select"
                          value={selfRef.toString() || ''}
                          disabled={getIsSelfReferOptionDisabled()}
                          onChange={(event) => {
                            setSelfRef(event.target.value === 'true');
                          }}
                        >
                          <option value={'false'}>{t('No')}</option>
                          <option value={'true'}>{t('Yes')}</option>
                        </Input>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col xs="2" style={{ display: isClientAdmin ? '' : 'none' }}>
                    <FormGroup>
                      <input type="hidden" value="something" />
                      <Label for="title">
                        {t('Client')}
                        <RequiredField />
                      </Label>
                      <div className="addm-select">
                        <Input
                          id="client"
                          name="client"
                          data-testid="client-select-input"
                          value={clientId || ''}
                          onChange={(event) => onChangeClient(event.target.value)}
                          type="select"
                          style={{ textOverflow: 'ellipsis', paddingRight: '1.5rem' }}
                          required
                        >
                          <option value="">{t('Select')}</option>
                          {clientList?.map((item, i) => {
                            return (
                              <option key={'client' + i} value={item.client_id}>
                                {item.company_name}
                              </option>
                            );
                          })}
                        </Input>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col xs="4">
                    <FormGroup>
                      <Label for="email">
                        {t('Email Address')}
                        <RequiredField />
                      </Label>
                      <Input
                        className="addm-email addm-gray-ph"
                        id="email"
                        data-testid="email-input"
                        type="email"
                        autoComplete="emailsetter"
                        value={formData.email || ''}
                        disabled={!!savedPatientDetails}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            email: event.target.value
                          });
                        }}
                        placeholder={t('Enter Email')}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="4">
                    <FormGroup>
                      <Label for="ContactNumber" style={{ fontSize: '15px', marginBottom: '15px' }}>
                        {t('Primary Contact Number')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <PhoneInput
                        placeholder={t('Enter phone number')}
                        data-testid="primary-contact-number-input"
                        defaultCountry="GB"
                        value={primaryContactNumber || ''}
                        onChange={(value) => {
                          setPrimaryContactNumber(value);
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="4">
                    <FormGroup>
                      <Label for="membership_no">
                        {t('Patient Reference')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <Input
                        className="addm-gray-ph addm-mt-3"
                        type="text"
                        data-testid="reference-input"
                        autoComplete="membershipn"
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            membership_no: event.target.value
                          })
                        }
                        value={formData.membership_no || ''}
                        disabled={ro_fields.includes('membership_no')}
                        placeholder={t('Enter Policy Number e.g A1B1')}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Row>
                    {clientSupportsInfoTags && (
                      <Col xs="4">
                        <FormGroup>
                          <Label for="ContactNumber" style={{ fontSize: '15px', marginBottom: '15px' }}>
                            {t('Info Tags')}
                            {!isClientAdmin && <span className="vl-product-satsuma">*</span>}
                          </Label>
                          <PatientTags
                            data-testid="info-tags-select-input"
                            patient_guid={formData.user_guid}
                            insured={true}
                            updateTags={setTags}
                            displayAlertMessage={displayAlertMessage}
                            isExternal={savedPatientDetails}
                            selectedTags={tags}
                          />
                        </FormGroup>
                      </Col>
                    )}
                    <Col xs={clientSupportsInfoTags ? '4' : '6'}>
                      <FormGroup>
                        <Label for="ContactNumber" style={{ fontSize: '15px', marginBottom: '15px' }}>
                          {t('Filter Tags')}
                        </Label>
                        <PatientTags
                          data-testid="filter-tags-select-input"
                          patient_guid={formData.user_guid}
                          insured={true}
                          updateTags={setFilterTags}
                          displayAlertMessage={displayAlertMessage}
                          isExternal={savedPatientDetails}
                          selectedTags={filterTags}
                          isFilter
                        />
                      </FormGroup>
                    </Col>
                    <Col xs={clientSupportsInfoTags ? '4' : '6'}>
                      <FormGroup>
                        <Label for="employer_code" style={{ fontSize: '15px', marginBottom: '15px' }}>
                          {t('Employer')}
                        </Label>
                        <Input
                          value={formData.employer_code || ''}
                          onChange={(e) => {
                            setFormData({ ...formData, employer_code: e.target.value });
                          }}
                          type="select"
                          name="employer_code"
                          id="employer_code"
                          data-testid="employer-select-input"
                        >
                          <option value="">{t('No Employer')}</option>
                          {employerList.map((item, i) => {
                            return (
                              <option key={'emp' + i} value={item.employer_code}>
                                {item.employer_desc}
                              </option>
                            );
                          })}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Col className="vl-flex-row-end">
                    {!fullPageMode && showExpandOption && (
                      <Button
                        className="vl-button vl-button-gray vl-font-regular"
                        type="button"
                        onClick={() => {
                          setSize('full');
                        }}
                        data-testid="expand-member-details"
                      >
                        {t('Expand')}
                      </Button>
                    )}
                    <div>
                      <Button
                        className="vl-button vl-button-gray vl-font-regular"
                        type="button"
                        onClick={() => {
                          cancel?.();
                          toggle();
                        }}
                        data-testid="cancel-save-patient"
                      >
                        {t('Cancel')}
                      </Button>

                      <LoadingButton
                        className="vl-button vl-font-regular"
                        onClick={() => {
                          handleSubmit();
                        }}
                        loading={saveInProgress}
                        disabled={saveInProgress}
                        data-testid="save-patient"
                      >
                        {getButtonText()}
                      </LoadingButton>
                    </div>
                  </Col>
                </Row>
              </fieldset>
            </form>
          </Col>
        </Row>
      </Container>
    );
  }

  const isInsuredFieldShown = savedPatientDetails;
  const isEssCommsOnlyFieldShown = !!savedPatientDetails && isClientAdmin;

  return (
    <Container fluid={true}>
      <Row>
        <Col xs={fullPageMode ? '10' : '12'}>
          <span className="vl-modal-title">{getTitle()} a</span>
          <span className="addm-member">{t('Patient')}</span>
        </Col>
        {fullPageMode && (
          <Col xs="2">
            <Button
              className="vl-button vl-button-gray vl-font-regular"
              type="button"
              onClick={() => {
                cancel?.();
                toggle();
              }}
              data-testid="cancel-save-patient"
            >
              {t('Cancel')}
            </Button>

            <LoadingButton
              className="vl-button vl-font-regular"
              onClick={() => {
                handleSubmit();
              }}
              loading={saveInProgress}
              disabled={saveInProgress}
              data-testid="save-patient"
            >
              {getButtonText()}
            </LoadingButton>
          </Col>
        )}
        <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
        <br />
        <Col xs="12">
          <form className="mt-5" data-testid="edit-patient-details">
            <fieldset disabled={readonly}>
              <Row className="addm-container">
                <Col xs="3">
                  <FormGroup>
                    <input type="hidden" value="something" />
                    <Label for="title">
                      {t('Title')}
                      <RequiredField />
                    </Label>
                    <div className="addm-select">
                      <Input
                        data-testid="title-select-input"
                        id="title"
                        name="title"
                        value={formData.title || ''}
                        disabled={ro_fields.includes('title')}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            title: event.target.value
                          })
                        }
                        type="select"
                        required
                      >
                        <option disabled value="">
                          {t('Select')}
                        </option>
                        {titles.map((item, i) => {
                          return (
                            <option key={'t' + i} value={item.title || ''}>
                              {item.title}
                            </option>
                          );
                        })}
                      </Input>
                    </div>
                  </FormGroup>
                </Col>
                <Col xs="3">
                  <FormGroup>
                    <Label for="first_name">
                      {t('First Name')}
                      <RequiredField />
                    </Label>
                    <Input
                      data-testid="first-name-input"
                      className="addm-first-name addm-gray-ph"
                      name={'a' + getRandomInt(0, 9)}
                      type="text"
                      autoComplete="fname"
                      value={formData.first_name || ''}
                      disabled={ro_fields.includes('first_name')}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          first_name: sanitiseValueFromAngleBrackets(event.target.value)
                        })
                      }
                      placeholder={t('Enter First Name')}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col xs="3">
                  <FormGroup>
                    <Label for="last_name">
                      {t('Last Name')}
                      <RequiredField />
                    </Label>
                    <Input
                      data-testid="last-name-input"
                      className="addm-last-name addm-gray-ph"
                      id="last_name"
                      name="last_name"
                      type="text"
                      autoComplete="lname"
                      value={formData.last_name || ''}
                      disabled={ro_fields.includes('last_name')}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          last_name: sanitiseValueFromAngleBrackets(event.target.value)
                        })
                      }
                      placeholder={t('Enter Last Name')}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col xs="3">
                  <FormGroup>
                    <Label for="pref_name">{t('Preferred Name')}</Label>
                    <Input
                      data-testid="preferred-name-input"
                      className="addm-pref-name addm-gray-ph"
                      id="pref_name"
                      name="pref_name"
                      type="text"
                      value={formData.pref_name || ''}
                      disabled={ro_fields.includes('pref_name')}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          pref_name: event.target.value
                        })
                      }
                      placeholder={t('Patient Preferred Name')}
                    />
                  </FormGroup>
                </Col>
                <Col xs="3">
                  <FormGroup>
                    <Label for="self-refer">
                      {t('Self Refer')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <div>
                      <Input
                        data-testid="self-refer-select-input"
                        type="select"
                        value={selfRef.toString() || ''}
                        disabled={getIsSelfReferOptionDisabled()}
                        onChange={(event) => {
                          setSelfRef(event.target.value === 'true');
                        }}
                      >
                        <option value={'false'}>{t('No')}</option>
                        <option value={'true'}>{t('Yes')}</option>
                      </Input>
                    </div>
                  </FormGroup>
                </Col>

                <Col xs="3">
                  <FormGroup>
                    <Label for="dob" style={{ marginBottom: '10px' }}>
                      {t('Date of Birth')}
                      <RequiredField />
                    </Label>
                    <VLDatePicker
                      data-testid="date-of-birth-select-input"
                      className="vl-width-full"
                      selected={dpDate && moment(dpDate).tz('Europe/London').format('LL')}
                      onChange={(date) => !readonly && setDpDate(date)}
                      disabled={ro_fields.includes('dob')}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="00/00/0000"
                    />
                  </FormGroup>
                </Col>
                <Col xs="3">
                  <FormGroup>
                    <Label for="sex">
                      {t('Sex')}
                      <RequiredField />
                    </Label>
                    <div className="addm-select">
                      <Input
                        id="sex"
                        data-testid="sex-select-input"
                        name="sex"
                        type="select"
                        style={{ minWidth: '80px' }}
                        value={formData.sex_code || ''}
                        disabled={ro_fields.includes('sex')}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            sex_code: event.target.value,
                            sex_desc: sexes.find((sex) => sex.sex_code === event.target.value)?.sex_desc,
                            sex: event.target.value
                          })
                        }
                        required
                      >
                        <option disabled value="">
                          {t('Select')}
                        </option>
                        {sexes.map((item, i) => {
                          return (
                            <option key={'s' + i} value={item.sex_code}>
                              {item.sex_desc}
                            </option>
                          );
                        })}
                      </Input>
                    </div>
                  </FormGroup>
                </Col>
                <Col xs="3">
                  <FormGroup>
                    <Label for="email">
                      {t('Email Address')}
                      <RequiredField />
                    </Label>
                    <Input
                      className="addm-email addm-gray-ph"
                      id="email"
                      type="email"
                      autoComplete="emailsetter"
                      data-testid="email-address-input"
                      value={formData.email || ''}
                      disabled={!!savedPatientDetails}
                      onChange={(event) => {
                        setFormData({
                          ...formData,
                          email: event.target.value
                        });
                      }}
                      placeholder={t('Enter Email')}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col xs="4" style={{ display: isClientAdmin ? '' : 'none' }}>
                  <FormGroup>
                    <input type="hidden" value="something" />
                    <Label for="title">
                      {t('Client')}
                      <RequiredField />
                    </Label>
                    <div className="addm-select">
                      <Input
                        id="client"
                        name="client"
                        data-testid="client-select-input"
                        value={clientId || ''}
                        onChange={(event) => onChangeClient(event.target.value)}
                        type="select"
                        required
                        style={{ textOverflow: 'ellipsis', paddingRight: '1.5rem' }}
                      >
                        <option disabled value="">
                          {t('Select')}
                        </option>
                        {clientList?.map((item, i) => {
                          return (
                            <option key={'client' + i} value={item.client_id}>
                              {item.company_name}
                            </option>
                          );
                        })}
                      </Input>
                    </div>
                  </FormGroup>
                </Col>
                {isInsuredFieldShown && (
                  <Col xs="4">
                    <FormGroup>
                      <Label for="sex">
                        {t('Insured')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <div className="addm-select">
                        <Input
                          id="insure"
                          name="insure"
                          type="select"
                          data-testid="insured-select-input"
                          style={{ minWidth: '80px' }}
                          value={formData.insured.toString() || ''}
                          onChange={(event) => {
                            const value = event.target.value === 'true';
                            setFormData({
                              ...formData,
                              insured: value
                            });
                          }}
                          required
                        >
                          <option disabled value="">
                            {t('Select')}
                          </option>
                          <option value={'true'}>{t('Yes')}</option>
                          <option value={'false'}>{t('No')}</option>
                        </Input>
                      </div>
                    </FormGroup>
                  </Col>
                )}
                {formData.insured !== false && (
                  <Row>
                    {clientSupportsInfoTags && (
                      <Col xs="6">
                        <FormGroup>
                          <Label for="ContactNumber" style={{ fontSize: '15px', marginBottom: '15px' }}>
                            {t('Info Tags')}
                            {!isClientAdmin && <span className="vl-product-satsuma">*</span>}
                          </Label>
                          <PatientTags
                            data-testid="info-tags-select-input"
                            patient_guid={formData.user_guid}
                            insured={true}
                            updateTags={setTags}
                            displayAlertMessage={displayAlertMessage}
                            isExternal={savedPatientDetails}
                            selectedTags={tags}
                          />
                        </FormGroup>
                      </Col>
                    )}

                    <Col xs="6">
                      <FormGroup>
                        <Label for="ContactNumber" style={{ fontSize: '15px', marginBottom: '15px' }}>
                          {t('Filter Tags')}
                        </Label>
                        <PatientTags
                          data-testid="filter-tags-select-input"
                          patient_guid={formData.user_guid}
                          insured={true}
                          updateTags={setFilterTags}
                          displayAlertMessage={displayAlertMessage}
                          isExternal={savedPatientDetails}
                          selectedTags={filterTags}
                          isFilter
                        />
                      </FormGroup>
                    </Col>
                    <Col xs={clientSupportsInfoTags ? '4' : '6'}>
                      <FormGroup>
                        <Label for="employer_code" style={{ fontSize: '15px', marginBottom: '15px' }}>
                          {t('Employer')}
                        </Label>
                        <Input
                          data-testid="employer-select-input"
                          value={formData.employer_code || ''}
                          onChange={(e) => {
                            setFormData({ ...formData, employer_code: e.target.value });
                          }}
                          type="select"
                          name="employer_code"
                          id="employer_code"
                        >
                          <option value="">{t('No Employer')}</option>
                          {employerList.map((item, i) => {
                            return (
                              <option key={'emp' + i} value={item.employer_code}>
                                {item.employer_desc}
                              </option>
                            );
                          })}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                )}
                <Col xs="4">
                  <FormGroup>
                    <Label style={{ marginBottom: '4px' }}>{t('Timezone')}</Label>
                    <Select
                      name="timezone"
                      id="timezone"
                      data-testid="timezone-select-input"
                      options={metaTimezones?.map((item) => ({ value: item, label: item }))}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      value={
                        timezone || {
                          value: 'Europe/London',
                          label: 'Europe/London'
                        }
                      }
                      onChange={(data) => {
                        setTimezone(data);
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col xs="4">
                  <FormGroup>
                    <Label for="ContactNumber" style={{ fontSize: '15px', marginBottom: '15px' }}>
                      {t('Primary Contact Number')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <PhoneInput
                      placeholder={t('Enter phone number')}
                      data-testid="primary-contact-number"
                      defaultCountry="GB"
                      value={primaryContactNumber || ''}
                      onChange={(value) => {
                        setPrimaryContactNumber(value || '');
                      }}
                      onCopy={(event: any) => {
                        event.preventDefault();
                        const selection = document.getSelection();
                        event.clipboardData.setData('text/plain', selection?.toString().replace(/\s+/g, '') || '');
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col xs="4">
                  <FormGroup>
                    <Label for="ContactNumber" style={{ fontSize: '15px', marginBottom: '15px' }}>
                      {t('Alternative Contact Number')}
                    </Label>
                    <PhoneInput
                      data-testid="alternative-contact-number"
                      placeholder={t('Enter phone number')}
                      defaultCountry="GB"
                      value={alternativeContactNumber || ''}
                      onChange={(value) => {
                        setAlternativeContactNumber(value || '');
                      }}
                      onCopy={(event: any) => {
                        event.preventDefault();
                        const selection = document.getSelection();
                        event.clipboardData.setData('text/plain', selection?.toString().replace(/\s+/g, '') || '');
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col xs="2">
                  <FormGroup>
                    <Label for="Notifications" style={{ fontSize: '15px', marginBottom: '8px' }}>
                      {t('Notifications')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <Input
                      data-testid="notifications-select-input"
                      id="Notification"
                      name="Notification"
                      type="select"
                      disabled={!savedPatientDetails}
                      onChange={(event) => {
                        setFormData({
                          ...formData,
                          pref_notification: event.target.value
                        });
                        setNotificationChange((prevState) => !prevState);
                      }}
                      value={savedPatientDetails ? formData.pref_notification : 'EMAIL'}
                      className="basic-multi-select"
                    >
                      <option value="SMS">{t('SMS')}</option>
                      <option value="EMAIL">{t('Email')}</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col xs="4" style={{ display: isEssCommsOnlyFieldShown ? '' : 'none' }}>
                  <FormGroup>
                    <Label for="EssCommsOnly">
                      {t('Essential Messaging Only')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <div>
                      <Input
                        data-testid="essential-messaging-only-select-input"
                        id="EssCommsOnly"
                        name="EssCommsOnly"
                        type="select"
                        value={essCommsOnly.toString() || ''}
                        onChange={(event) => {
                          setEssCommsOnly(event.target.value === 'true');
                        }}
                      >
                        <option value={'false'}>{t('No')}</option>
                        <option value={'true'}>{t('Yes')}</option>
                      </Input>
                    </div>
                  </FormGroup>
                </Col>
                <Col xs="4">
                  <FormGroup>
                    <Label for="membership_no">
                      {t('Patient Reference')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <Input
                      data-testid="patient-reference-input"
                      className="addm-gray-ph addm-mt-3"
                      type="text"
                      autoComplete="membershipn"
                      disabled={ro_fields.includes('membership_no')}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          membership_no: sanitiseValueFromAngleBrackets(event.target.value)
                        })
                      }
                      value={formData.membership_no || ''}
                      placeholder={t('Enter Policy Number e.g A1B1')}
                      required
                    />
                  </FormGroup>
                </Col>

                <Col xs={isEssCommsOnlyFieldShown ? '2' : '6'} />

                <Col xs="6">
                  <FormGroup>
                    <Label for="postcode">
                      {t('Postcode')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <PostcodeAutocomplete
                      data-testid="postcode-autocomplete-input"
                      placeholder={t('Enter Postcode')}
                      valueEx={formData.postcode}
                      disabled={ro_fields.includes('postcode')}
                      onSelect={onSelect_postcode}
                      onManualChange={handleManualPostcodeChange}
                    />
                  </FormGroup>
                </Col>

                <Col
                  xs="6"
                  className={classnames({
                    'addm-opacity': !activeField
                  })}
                >
                  <FormGroup>
                    <Label for="addline1">
                      {t('Address Line 1')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <Input
                      id="addline1"
                      name="addline1"
                      data-testid="address-line-1-input"
                      type="text"
                      autoComplete="line1"
                      value={formData.addline1 || ''}
                      disabled={ro_fields.includes('addline1')}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          addline1: sanitiseValueFromAngleBrackets(event.target.value)
                        })
                      }
                      placeholder={t('Address Line 1')}
                      readOnly={!activeField}
                    />
                  </FormGroup>
                </Col>
                <Col
                  xs="6"
                  className={classnames({
                    'addm-opacity': !activeField
                  })}
                >
                  <FormGroup>
                    <Label for="addline2">{t('Address Line 2')}</Label>
                    <Input
                      data-testid="address-line-2-input"
                      className="addm-gray-ph"
                      type="text"
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          addline2: sanitiseValueFromAngleBrackets(event.target.value)
                        })
                      }
                      value={formData.addline2 || ''}
                      disabled={ro_fields.includes('addline2')}
                      placeholder={t('Enter Address Line 2')}
                    />
                  </FormGroup>
                </Col>
                <Col
                  xs="6"
                  className={classnames({
                    'addm-opacity': !activeField
                  })}
                >
                  <FormGroup>
                    <Label for="addline3">{t('Address Line 3')}</Label>
                    <Input
                      data-testid="address-line-3-input"
                      className="addm-gray-ph"
                      type="text"
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          addline3: sanitiseValueFromAngleBrackets(event.target.value)
                        })
                      }
                      value={formData.addline3 || ''}
                      disabled={ro_fields.includes('addline3')}
                      placeholder={t('Enter Address Line 3')}
                    />
                  </FormGroup>
                </Col>
                <Col
                  xs="6"
                  className={classnames({
                    'addm-opacity': !activeField
                  })}
                >
                  <FormGroup>
                    <Label for="region">{t('Town/City')}</Label>
                    <Input
                      data-testid="town-input"
                      id="town"
                      name="town"
                      type="text"
                      autoComplete="town"
                      value={formData.addline4 || ''}
                      disabled={ro_fields.includes('addline4')}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          addline4: sanitiseValueFromAngleBrackets(event.target.value)
                        })
                      }
                      placeholder={t('Town/City')}
                      readOnly={!activeField}
                    />
                  </FormGroup>
                </Col>
                <Col
                  xs="6"
                  className={classnames({
                    'addm-opacity': !activeField
                  })}
                >
                  <FormGroup>
                    <Label for="country">{t('County')}</Label>
                    <Input
                      data-testid="county-input"
                      id="county"
                      name="county"
                      type="text"
                      autoComplete="county"
                      value={formData.region || ''}
                      disabled={ro_fields.includes('region')}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          region: sanitiseValueFromAngleBrackets(event.target.value)
                        })
                      }
                      placeholder={t('County')}
                      readOnly={!activeField}
                    />
                  </FormGroup>
                </Col>

                {!readonly && !fullPageMode && (
                  <Col xs="12" className="vl-flex-row-end">
                    {!fullPageMode && showExpandOption && (
                      <Button
                        className="vl-button vl-button-gray vl-font-regular"
                        type="button"
                        onClick={() => {
                          setSize('short');
                        }}
                        data-testid="collapse-button"
                      >
                        {t('Collapse')}
                      </Button>
                    )}
                    <div>
                      <Button
                        className="vl-button vl-button-gray vl-font-regular"
                        type="button"
                        onClick={() => {
                          cancel?.();
                          toggle();
                        }}
                        data-testid="cancel-save-patient"
                      >
                        {t('Cancel')}
                      </Button>

                      <LoadingButton
                        className="vl-button vl-font-regular"
                        onClick={() => {
                          handleSubmit();
                        }}
                        loading={saveInProgress}
                        disabled={saveInProgress}
                        data-testid="save-patient"
                      >
                        {getButtonText()}
                      </LoadingButton>
                    </div>
                  </Col>
                )}
              </Row>
            </fieldset>
          </form>
        </Col>
      </Row>
    </Container>
  );
};

export const AddPatient = ({
  close,
  show,
  savedPatientDetails = undefined,
  onCreated = (data?: unknown) => {},
  initialSize = 'full',
  ...props
}) => {
  const [size, setSize] = useState(initialSize);

  useEffect(() => {
    setSize(initialSize);
  }, [initialSize]);

  useEffect(() => {
    if (!show) {
      setSize(initialSize);
    }
  }, [show]);

  return (
    <Modal
      centered
      wrapClassName="vl-common-bootstrap vl-common-add-patient"
      className="vl-add-modal addm-modal"
      size={'lg'}
      isOpen={show}
      // toggle={toggle}
      backdrop={'static'}
      keyboard={false}
      data-testid="add-patient-modal"
    >
      <ModalBody className="p-5">
        <EditPatientDetails
          close={close}
          savedPatientDetails={savedPatientDetails}
          onCreated={onCreated}
          size={size}
          setSize={setSize}
          showExpandOption={initialSize === 'short'}
          {...props}
        />
      </ModalBody>
    </Modal>
  );
};

export default AddPatient;
